import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import BehaviorFish from '@/assets/icons/behavior_fish.svg?component';
import Respiration from '@/assets/icons/respiration.svg?component';
import SwimSpeed from '@/assets/icons/swim_speed.svg?component';
import Tooltip from '@/components/atoms/Tooltip';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import type { OverviewData } from '@/domain/behavior';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import {
    formatPercentageByLocale,
    formatSwimSpeedByLocale,
    formatUniversalUnitByLocale,
    parseLocale,
} from '@/utils/localeFormatters';

import {
    SWIM_SPEED_THRESHOLD_DEFAULT,
    TILT_THRESHOLD_DEFAULT,
} from '../../BehaviorDashboardContainer';
import BehaviorMetricCard from '../BehaviorMetricCard/BehaviorMetricCard';

interface BehaviorOverviewMetricsProps {
    overview: OverviewData;
    isLoading: boolean;
    /** Shown in place of the cards, which would otherwise read "-" as if nothing were measured. */
    error?: unknown;
    retry?: RetryProps;
}

/** The swim speed, active respiration and tilt averages at the top of the behaviour page. */
export default function BehaviorOverviewMetrics(props: BehaviorOverviewMetricsProps) {
    const { overview, isLoading, error, retry } = props;
    const { t, i18n } = useTranslation('behaviorModule');
    const locale = parseLocale(i18n.language);

    const shouldShowAverages = !overview.isBioscopeRecentlyDeployed;
    const swimSpeedAverageValue = formatSwimSpeedByLocale(
        shouldShowAverages ? overview.averages.swimSpeed : undefined,
        locale
    );
    const activeRespirationAverageValue = formatPercentageByLocale(
        shouldShowAverages ? overview.averages.activeRespiration : undefined,
        locale
    );
    const tiltAverageValue = formatUniversalUnitByLocale(
        shouldShowAverages ? overview.averages.tilt : undefined,
        '°',
        locale
    );

    const swimSpeedTrendType: 'elevated' | 'normal' | 'reduced' | 'none' = useMemo(() => {
        if (!overview.averages.swimSpeed || overview.isBioscopeRecentlyDeployed) {
            return 'none';
        }
        if (overview.averages.swimSpeed > SWIM_SPEED_THRESHOLD_DEFAULT.max) {
            return 'elevated';
        }
        if (overview.averages.swimSpeed < SWIM_SPEED_THRESHOLD_DEFAULT.min) {
            return 'reduced';
        }
        return 'normal';
    }, [overview.averages.swimSpeed, overview.isBioscopeRecentlyDeployed]);

    const tiltTrendType: 'elevated' | 'normal' | 'none' = useMemo(() => {
        if (!overview.averages.tilt || overview.isBioscopeRecentlyDeployed) {
            return 'none';
        }
        if (overview.averages.tilt > TILT_THRESHOLD_DEFAULT.optimal) {
            return 'elevated';
        }
        return 'normal';
    }, [overview.averages.tilt, overview.isBioscopeRecentlyDeployed]);

    if (error) {
        return (
            <ContentBlock spacing="none">
                <SomethingHappened onRetry={retry?.onRetry} isRetrying={retry?.isRetrying} />
            </ContentBlock>
        );
    }

    return (
        <GridLayout contentLayout="3-fluid">
            <ContentBlock spacing="none">
                <Tooltip
                    content={
                        shouldShowAverages
                            ? t('tooltips.swimSpeed')
                            : t('tooltips.isBioscopeRecentlyDeployed')
                    }
                    maxWidth={370}>
                    <BehaviorMetricCard
                        isLoading={isLoading}
                        title={t('common.swimSpeed')}
                        value={swimSpeedAverageValue}
                        icon={SwimSpeed}
                        tone="neutral"
                        trendType={swimSpeedTrendType}
                    />
                </Tooltip>
            </ContentBlock>

            <ContentBlock spacing="none">
                <Tooltip
                    content={
                        shouldShowAverages
                            ? t('tooltips.respiration')
                            : t('tooltips.isBioscopeRecentlyDeployed')
                    }
                    maxWidth={460}>
                    <BehaviorMetricCard
                        isLoading={isLoading}
                        title={t('overview.activeRespiration')}
                        value={activeRespirationAverageValue}
                        icon={Respiration}
                        tone="info"
                        trendType="none"
                        suffix={t('overview.measuredFish')}
                    />
                </Tooltip>
            </ContentBlock>

            <ContentBlock spacing="none">
                <Tooltip
                    content={
                        shouldShowAverages
                            ? t('tooltips.tilt')
                            : t('tooltips.isBioscopeRecentlyDeployed')
                    }
                    maxWidth={370}>
                    <BehaviorMetricCard
                        isLoading={isLoading}
                        title={t('common.tilt')}
                        value={tiltAverageValue}
                        icon={BehaviorFish}
                        tone="success"
                        trendType={tiltTrendType}
                    />
                </Tooltip>
            </ContentBlock>
        </GridLayout>
    );
}
